"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api, setToken, UserRole } from "./api";

interface Me {
  id: string;
  phone: string | null;
  telegram_id: number | null;
  roles: UserRole[];
  display_name: string | null;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  isTrainer: boolean;
  isAdmin: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await api.get<Me>("/auth/me");
      setMe(data);
    } catch {
      setMe(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage.getItem("access_token")) {
      refresh();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (token: string) => {
    setToken(token);
    await refresh();
  };

  const logout = () => {
    setToken(null);
    setMe(null);
  };

  const isTrainer = me?.roles.includes("trainer") ?? false;
  const isAdmin = me?.roles.includes("admin") ?? false;

  return (
    <AuthContext.Provider value={{ me, loading, isTrainer, isAdmin, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
